import { rgba } from 'polished';
import { colors } from '@ardoq/design-tokens';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { type Bounds, DEFAULT_MULTI_LABEL_STYLE } from '@ardoq/graph';
import { BlocksViewNode, BlocksViewVisualState } from './types';
import { ConditionalFormattingState } from '@ardoq/data-model';

export const BLOCKS_VIEW_FIT_PADDING = 30;

// layout constants

export const BLOCKS_VIEW_NODE_MINIMUM_WIDTH = 158; //  minimum grid row size for all nodes
export const BLOCKS_VIEW_NODE_MINIMUM_HEIGHT = 120; // minimum grid col size for all nodes
export const BLOCKS_VIEW_COLLAPSED_GROUP_MINIMUM_HEIGHT = 152;

export const BLOCKS_VIEW_NODECHANNEL_MINIMUM_SIZE = 16; // minimum grid row and col size for routing cols and rows
export const BLOCKS_VIEW_ROUTINGCHANNEL_MINIMUM_SIZE = 16; // minimum grid row and col size for routing cols and rows
export const BLOCKS_VIEW_ROUTEDCHANNEL_MINIMUM_SIZE = 160; // minimum grid row and col size for routed cols and rows
export const BLOCKS_VIEW_ROUTING_MARGIN = 16; // space between non-empty routing channels and nodes
export const BLOCKS_VIEW_ROUTING_SEP = 26; // space between routing lines

export const BLOCKS_VIEW_BADDROP_FILL = rgba(colors.red50, 0.6);

export const BLOCKS_VIEW_ALIGNMENT_STROKE = '#AF73FF';
export const BLOCKS_VIEW_ALIGNMENT_LINEDASH = [1, 2]; // pixels

export const BLOCKS_VIEW_CONTEXT_COLOR = '#2974C4'; // color for context node highlight
export const BLOCKS_VIEW_CONTEXT_WIDTH = 6;

export const BLOCKS_VIEW_NODE_MARGIN = 0; // node margin world units (space between cell and visible bounds)
export const BLOCKS_VIEW_NODE_PAD = 4; // node padding world units (minimum space between visible bounds and content)
export const BLOCKS_VIEW_NODE_SEP = 6; // space between internal components
export const BLOCKS_VIEW_NODE_BORDERRADIUS = 8;

export const BLOCKS_VIEW_LOWLIGHT_ALPHA = 0.2;

const BLOCKS_VIEW_MULTILABEL_FONTHEIGHT = 14;
export const BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT = 20;
export const BLOCKS_VIEW_MULTILABEL_FONT = `400 ${BLOCKS_VIEW_MULTILABEL_FONTHEIGHT}px/${BLOCKS_VIEW_MULTILABEL_LINE_HEIGHT}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
export const BLOCKS_VIEW_MULTILABEL_PADDING_HORIZONTAL = 8;
export const BLOCKS_VIEW_MULTILABEL_PADDING_VERTICAL = 2;

export const BLOCKS_VIEW_NODE_LABEL_COLOR = 'black';
export const BLOCKS_VIEW_NODE_LABEL_WRAP = 200;
export const BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT = 14;
export const BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT = 18;
export const BLOCKS_VIEW_NODE_LABEL_FONT = `500 ${BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT}px/${BLOCKS_VIEW_NODE_LABEL_LINE_HEIGHT}px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
export const BLOCKS_VIEW_NODE_SUBLABEL_FONT = `italic ${BLOCKS_VIEW_NODE_LABEL_FONTHEIGHT}px/20px ${ARDOQ_DEFAULT_FONT_FAMILY}`;

export const BLOCKS_VIEW_GROUP_BORDERCOLOR = colors.grey300;
export const BLOCKS_VIEW_GROUP_BORDERWIDTH = 1.0;
export const BLOCKS_VIEW_GROUP_BACKGROUNDCOLOR = colors.grey50;
export const BLOCKS_VIEW_GROUP_BADGE_FILLCOLOR = colors.black950; // group expand button fill
export const BLOCKS_VIEW_GROUP_BADGE_COLOR = colors.white950; // group badge button stroke
export const BLOCKS_VIEW_GROUP_BADGE_FONT_SIZE = 12;
export const BLOCKS_VIEW_GROUP_BADGE_FONT = `${BLOCKS_VIEW_GROUP_BADGE_FONT_SIZE}px ${ARDOQ_DEFAULT_FONT_FAMILY}`; // group badge font
export const BLOCKS_VIEW_GROUP_BADGE_SIZE = 24; // minimum  group badge size

export const BLOCKS_VIEW_MULTILABEL_BORDERRADIUS = 4;

export const BLOCKS_VIEW_LEAF_BORDERCOLOR = colors.grey300;
export const BLOCKS_VIEW_LEAF_BORDERWIDTH = 0.75;
export const BLOCKS_VIEW_LEAF_BACKGROUNDCOLOR = colors.white950;
export const BLOCKS_VIEW_LEAF_RD_OUTERSIZE = 58; // width and height of leaf node "icon"
export const BLOCKS_VIEW_LEAF_RD_INNERSIZE = 50; // width and height of leaf node "icon"
export const BLOCKS_VIEW_LEAF_ICON_SIZE = 24; // size of icon inside the circle
export const BLOCKS_VIEW_LEAF_LABEL_MARGIN = 3;

export const BLOCKS_VIEW_LINK_LINEWIDTH = 0.75; // line nominal line thickness (world units)
export const BLOCKS_VIEW_LINK_LINEMINWIDTH = 1.0; // line minimum line thickness (device units)
export const BLOCKS_VIEW_LINK_MARKER_SIZE = 4; // edge end marker size (world units)
export const BLOCKS_VIEW_LINK_LABEL_FONT = `16px ${ARDOQ_DEFAULT_FONT_FAMILY}`;
export const BLOCKS_VIEW_LINK_LABEL_HEIGHT = 10;
export const BLOCKS_VIEW_LINK_LABEL_COLOR = colors.black950;
export const BLOCKS_VIEW_LINK_LABEL_FILLCOLOR = colors.white950;
export const BLOCKS_VIEW_LINK_LABEL_PADDING = 4;
export const BLOCKS_VIEW_LINK_LABEL_MAXLENGTH = 168;
export const BLOCKS_VIEW_HIGHLIGHT_COLOR = rgba(colors.green600, 0.35); // color for highlit nodes and links
export const BLOCKS_VIEW_LINK_CORNER_RADIUS = 10;
export const BLOCKS_VIEW_LINK_HOVER_BLOATRATIO = 5; // amount that links get bloated by when hovered

export const BLOCKS_VIEW_HANDLE_RENDERSIZE = 6; // editing handle size (pixels)
export const BLOCKS_VIEW_HANDLE_GRABSIZE = 13; // editing handle size (pixels)
export const BLOCKS_VIEW_HANDLE_COLOR = colors.black950; // editing handle stroke
export const BLOCKS_VIEW_HANDLE_FILLCOLOR = colors.white950; // editing handle fill

export const BLOCKS_VIEW_EDITPREVIEW_DROPFILL = '#D4DAE3';

export const BLOCKS_VIEW_URL_ICON_SIZE = 16;
/** the height of the header inside its padding, where the expander, label, and badge are drawn. */
export const BLOCKS_VIEW_HEADER_INNER_HEIGHT = 20;
export const BLOCKS_VIEW_EXPANDER_MARGIN_RIGHT = 2;
export const BLOCKS_VIEW_URL_ICON_MARGIN_LEFT = 2;
export const INVALID_RECTANGLE_SIDES: Bounds = [NaN, NaN, NaN, NaN];

export const NODE_DEFAULTS: Omit<BlocksViewNode, 'id'> = {
  col: NaN,
  colSpan: NaN,
  row: NaN,
  rowSpan: NaN,
  width: 60,
  height: 60,
  visualState: BlocksViewVisualState.None,
  isComponent: false,
  groupType: null,
  conditionalFormattingState: ConditionalFormattingState.NORMAL,
  urlFieldValues: null,

  bounds: [0, 0, 1, 1] satisfies Bounds,
  links: [],
  modelId: 'xyzzy',
  representationData: null,
  labels: [''],
  multiLabelStyle: DEFAULT_MULTI_LABEL_STYLE,
  displayLabel: [],
  isContext: false,
  open: true,
  color: colors.transparent0,
  animation: null,
  parent: null,
  children: null,
  descendantCount: 0,
};
